import { useTranslation } from '@mukil/i18n';
import { Badge, Button, GlassCard } from '@jumpstone/ui-react';
import { ACTION_ITEM_KEY } from '../../lib/label_keys';
import { navigate } from '../../lib/router';
import type { Route } from '../../lib/router';
import type { ActionItem, ActionItemKind } from '../../data/types';

const ACTION_ROUTE: Record<ActionItemKind, Route> = {
  expenses_pending: { module: 'expenses', tab: 'pending' },
  update_requests_open: { module: 'update_requests' },
  permits_expiring: {
    module: 'employees',
    view: 'profile',
    employee_id: 'e1',
    tab: 'job',
  },
  joiners_missing_bank_details: { module: 'payroll', tab: 'run' },
};

/**
 * Work queued for the payroll administrator, each row deep-linking to it.
 * When every real queue is at 0 (`items` empty -- `resolveDashboardState`
 * drops zero-count rows entirely, issue #24350) the card shows a "nothing
 * needs your attention" empty copy rather than an empty header.
 */
export function ActionNeededCard({
  items,
}: {
  items: ActionItem[];
}): React.JSX.Element {
  const { t } = useTranslation('wp');

  return (
    <GlassCard className="rounded-2xl p-5">
      <h2 className="text-[14.5px] font-bold mb-2.5">
        {t('dashboard.action_needed')}
      </h2>
      {items.length === 0 ? (
        <p className="text-[13px] text-[color:var(--ink-muted)]">
          {t('dashboard.action_needed_empty')}
        </p>
      ) : (
        items.map((item) => (
          <div key={item.kind} className="wp-listrow">
            <span className="flex items-center gap-2.5">
              <Badge variant={item.tone} className="num">
                {item.count}
              </Badge>
              <span>
                {t(ACTION_ITEM_KEY[item.kind], { count: item.count })}
              </span>
            </span>
            <Button
              variant="secondary"
              size="xs"
              onClick={() => {
                navigate(ACTION_ROUTE[item.kind]);
              }}
            >
              {t('common.review')}
            </Button>
          </div>
        ))
      )}
    </GlassCard>
  );
}
